// The Briefing card of an OPEN EMAIL (Pouria, 2026-09-22 — the "open email" section of the Figma,
// 40011435:288021: frames 40011517:200309, 40011598:356726, 40011618:361324, 40011626:363587,
// 40011700:370520, 40011700:370990 / 40011702:373306).
//
// Every frame that has a conversation in the middle heads the box with "Briefing . <subject>" and
// lists what the thread CARRIES, one fact a row:
//
//   OpenAI                    the other party — by NAME, the address on hover (busy-my-web#611)
//   2 Participants            the OTHER people on the thread, when more than one (not me, #616)
//   2 Attachments             files on its messages
//   1 Task / 1 Note / 1 Event what has been created ON it
//   Conversation              once the box has been asked about it (busy-my-web#524)
//   1 possible ongoing match  the offers waiting on it (busy-my-web#316)
//   Thread important · 1 message important   (the scheduled-mail frames, once starred)
//
// …and on the title line's right, the thread's own verbs — star · archive · trash · mark-unread ·
// more (Print / Mark as unread / Report as spam), plus "Unsubscribe" for a list mail — which the
// reader's page header used to draw and the frames do not. Those are components/Email/
// EmailThreadCardActions.vue; this file only says what the card SAYS.
//
// Pure: the store hands in the timeline it already holds and the two counts it already knows.
//
// The card's HEADER is a breadcrumb since busy-my-web#620 (Saffet 2026-10-09, "purple = where I am
// now"): "Home › Briefing › ⟨One workspace name starting…⟩" — "Briefing" a grey crumb, the SUBJECT
// the lavender chip. lib/briefings/trail.js builds that from the card's own `subject`; `title`
// keeps the frames' "Briefing . <subject>" for a card the trail cannot be drawn over.

import { briefing, countLabel } from './rows'
import { messageNames, participantDisplay } from '@/lib/senderDisplay'

export const THREAD_KIND = 'email-thread'

// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
// "2 Possible ongoing matches", "3 Messages important".
const plural = (n, word, pluralWord = `${word}s`) => countLabel(n, word, pluralWord)

const address = (p) => String(p?.email || p?.emailAddress || '').trim()

/** The `List-Unsubscribe` door, when a message carries one under any of the spellings seen. */
export function unsubscribeLink(messages) {
  for (const m of messages || []) {
    const link = m?.unsubscribeUrl || m?.listUnsubscribe || m?.unsubscribeLink || ''
    if (link) return String(link)
  }
  return ''
}

/**
 * @param timeline  the canonical thread (`timeline/getTimelineById`)
 * @param me        the viewer's address, so it is not listed as the other party
 * @param matches   how many possible-Ongoing cards wait on this thread (`threadMatchCount`, #612)
 * @param conversation whether the box already holds a conversation about it
 */
export function emailThreadBriefing(timeline, { me = '', matches = 0, conversation = false } = {}) {
  if (!timeline || !timeline.id) return null
  const subject = String(timeline.headLine || timeline.subject || '').trim() || 'Untitled'
  const messages = (timeline.emailMessages || []).filter(m => m && m.isDeleted !== true)
  const mine = String(me || '').trim().toLowerCase()
  const participants = (timeline.participants || []).filter(p => address(p))
  const others = participants.filter(p => address(p).toLowerCase() !== mine)
  // The other party is NAMED the way the reader's own header names them ("OpenAI", not
  // "noreply@email.openai.com" — busy-my-web#611, Saffet 2026-10-09): the estate's one rule, with the
  // thread's own From/To lines asked where the participant record carries no name. The address is
  // not lost — it rides on the row as its hover text.
  const senderAddress = address(others[0])
  const sender = senderAddress ? participantDisplay(others[0], null, messageNames(messages)) : ''
  const attachments = messages.reduce((n, m) => n + (Array.isArray(m.attachments) ? m.attachments.length : (m.hasAttachments ? 1 : 0)), 0)
  const items = Array.isArray(timeline.timelineItems) ? timeline.timelineItems : []
  const count = (type) => items.filter(i => i && Number(i.itemType) === type).length
  const importantMessages = messages.filter(m => m.isImportant).length

  const rows = []
  if (timeline.isImportant) rows.push({ key: 'important', icon: 'important', text: 'Thread important' })
  if (importantMessages) rows.push({ key: 'important-messages', icon: 'important', text: plural(importantMessages, 'message important', 'messages important') })
  if (sender) rows.push({ key: 'sender', icon: 'user', text: sender, ...(sender !== senderAddress ? { hint: senderAddress } : {}) })
  // The viewer is not "a participant" of their own mail (Pouria, 2026-10-09, busy-my-web#616): the
  // row counts the OTHER people on the thread, and a single other party is already the sender row.
  if (others.length > 1) rows.push({ key: 'participants', icon: 'contacts', text: `${others.length} Participants` })
  if (attachments) rows.push({ key: 'attachments', icon: 'paperclip', text: plural(attachments, 'Attachment') })
  const tasks = count(2)
  const events = count(3)
  const notes = count(4)
  if (tasks) rows.push({ key: 'tasks', icon: 'tick-circle', text: plural(tasks, 'Task') })
  if (events) rows.push({ key: 'events', icon: 'calendar', text: plural(events, 'Event') })
  if (notes) rows.push({ key: 'notes', icon: 'notes', text: plural(notes, 'Note') })
  // Both of these are ways IN (Pouria, 2026-09-22): the conversation reopens over the mail, the
  // matches open their cards — the same door the inbox card's row uses (busy-my-web#316/#317).
  if (conversation) rows.push({ key: 'conversation', icon: 'message', text: 'Conversation', open: 'conversation' })
  // …and the matches row names WHICH thread it is about (busy-my-web#612, Saffet 2026-10-09), so
  // pressing it opens this email's own cards rather than the mailbox's — `matches` is the number of
  // those cards (lib/possibleMatches' `threadMatchCount`), the same count the crumb then repeats.
  if (matches > 0) rows.push({ key: 'matches', icon: 'radar-2', text: plural(matches, 'possible ongoing match', 'possible ongoing matches'), relations: true, relationsThread: String(timeline.id) })

  return {
    ...briefing({
      kind: THREAD_KIND,
      title: `Briefing . ${subject}`,
      // The frames head this card with the words alone — no glyph before "Briefing".
      titleIcon: '',
      titleColor: '#111827',
      rows,
    }),
    // The thread's subject on its own, for the crumb's chip (busy-my-web#620) — read here rather
    // than by stripping "Briefing . " off `title`, so the two cannot drift.
    subject,
    // What the card's verbs act on — see EmailThreadCardActions.vue.
    thread: {
      id: String(timeline.id),
      isImportant: !!timeline.isImportant,
      isArchive: !!timeline.isArchive,
      seen: timeline.seen !== false,
      unsubscribe: unsubscribeLink(messages),
    },
  }
}
